import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import { useNavigate } from "react-router-dom";
import { EmailCampaignService } from "../../services/EmailCampaignService";
import {
  EmailCampaignData,
  SaveEmailCampaignConfigsRequest,
  EmailStylingFormData,
  MerchantEmailCampaignSettings
} from "../../types/EmailCampaignTypes";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CustomDataTable from "../../components/customTable/CustomDataTable";
import CustomButton from "../../components/CommonButton/CommonButton";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import Loader from "../../components/loader/loader";
import "./EmailCampaigns.css";
import UploadFile from "../../Utils/uploadFile";
import SadFaceImage from "../../assets/images/image_sadface.png";
import { useAppSelector } from "../../redux/hooks";

const EmailCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [scheduledCampaigns, setScheduledCampaigns] = useState<
    EmailCampaignData[]
  >([]);
  const [previousCampaigns, setPreviousCampaigns] = useState<
    EmailCampaignData[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openRates, setOpenRates] = useState<{
    [key: string]: { TotalSent: number; TotalOpened: number; OpenRate: number };
  }>({});
  console.log("🚀 ~ EmailCampaigns ~ openRates:", openRates);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Get business details from localStorage
  const [businessData, setBusinessData] = useState<any>(null);
  const [businessToken, setBusinessToken] = useState<string | null>(null);

  // Email styling state
  const [emailStyling, setEmailStyling] = useState<EmailStylingFormData>({
    headerBgColor: "#333333",
    headerFontColor: "#ffffff",
    contentBorderColor: "#cccccc",
    emailCampaignLogo: ""
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [stylingLoading, setStylingLoading] = useState(false);
  const [stylingSuccess, setStylingSuccess] = useState<string | null>(null);
  const [merchantSettings, setMerchantSettings] =
    useState<MerchantEmailCampaignSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Logo upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Global billing / restriction state from Redux
  const { isNonPaying, isBillingLoading } = useAppSelector(
    (s) => s.businessAccess
  );

  const loadCampaigns = useCallback(async () => {
    if (!businessData?.BusinessId || !businessToken) return;

    try {
      setLoading(true);
      setError(null);

      const emailCampaignService = new EmailCampaignService();

      // Fire settings load in parallel without affecting main page loader
      (async () => {
        try {
          setSettingsLoading(true);
          const settings = await emailCampaignService
            .loadMerchantEmailCampaignSettings(businessData.BusinessId)
            .catch(() => null);

          setMerchantSettings(settings);
          if (settings) {
            setEmailStyling({
              headerBgColor: settings.HeaderBgColor,
              headerFontColor: settings.HeaderFontColor,
              contentBorderColor: settings.ContentBorderColor,
              emailCampaignLogo: settings.EmailCampaignLogo
            });
          }
        } catch (e) {
          // ignore settings load errors here; do not block main UI
        } finally {
          setSettingsLoading(false);
        }
      })();

      // Load main campaigns data first
      const [outgoing, previous] = await Promise.all([
        emailCampaignService.getOutgoingCampaigns(businessData.BusinessId),
        emailCampaignService.getPreviousCampaigns(businessData.BusinessId)
      ]);

      setScheduledCampaigns(outgoing);
      setPreviousCampaigns(previous);

      // Load open rates in parallel in the background without blocking main UI
      (async () => {
        const rates: {
          [key: string]: {
            TotalSent: number;
            TotalOpened: number;
            OpenRate: number;
          };
        } = {};

        // Create all open rate promises at once for parallel execution
        const openRatePromises = previous.map(async (campaign) => {
          try {
            const rate = await emailCampaignService.getEmailCampaignOpenRate(
              campaign.CampaignId
            );
            console.log(`Campaign ${campaign.CampaignId} rate response:`, rate);
            return {
              campaignId: campaign.CampaignId,
              stats: {
                TotalSent: rate?.TotalSent || 0,
                TotalOpened: rate?.TotalOpened || 0,
                OpenRate: rate?.OpenRate || 0
              }
            };
          } catch (error) {
            console.error(
              `Error loading rate for campaign ${campaign.CampaignId}:`,
              error
            );
            return {
              campaignId: campaign.CampaignId,
              stats: {
                TotalSent: 0,
                TotalOpened: 0,
                OpenRate: 0
              }
            };
          }
        });

        // Wait for all open rate requests to complete
        const results = await Promise.all(openRatePromises);

        // Update rates state with all results
        results.forEach(({ campaignId, stats }) => {
          rates[campaignId] = stats;
        });

        setOpenRates(rates);
      })();
    } catch (error) {
      console.error("Error loading campaigns:", error);
      setError("Failed to load email campaigns. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [businessData?.BusinessId, businessToken]);

  // Initialize business data from localStorage
  useEffect(() => {
    const currentBusiness = localStorage.getItem("currentBusiness");
    const token = localStorage.getItem("businessAccessToken");

    if (currentBusiness) {
      setBusinessData(JSON.parse(currentBusiness));
    }
    setBusinessToken(token);
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Auto-scroll to error when it appears (with offset for fixed headers)
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        const element = errorRef.current as HTMLDivElement;
        const headerOffset = 120;
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        const scrollTarget = Math.max(elementTop - headerOffset, 0);
        window.scrollTo({ top: scrollTarget, behavior: "smooth" });
        setTimeout(() => element.focus({ preventScroll: true }), 200);
      }, 0);
    }
  }, [error]);

  const handleResendCampaign = useCallback(
    (campaignId: string) => {
      // Navigate to edit page with campaign ID for resending
      navigate(`/emailcampaigns/duplicate/${campaignId}`);
    },
    [navigate]
  );

  const handleStylingChange = (
    field: keyof EmailStylingFormData,
    value: string
  ) => {
    setEmailStyling((prev) => ({ ...prev, [field]: value }));

    // Reset file upload state when switching away from upload option
    if (field === "emailCampaignLogo" && value !== "upload") {
      setSelectedFile(null);
      setLogoPreview(null);
      setUploadError(null);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setUploadError("Please select a valid image file (JPEG)");
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setUploadError("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setUploadError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setLogoPreview(null);
    setUploadError(null);
    setEmailStyling((prev) => ({ ...prev, emailCampaignLogo: "" }));

    // Reset file input
    const fileInput = document.getElementById(
      "logo-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSaveStyling = async () => {
    if (!businessData?.BusinessId || !businessToken) {
      setError("Business information not found. Please log in again.");
      return;
    }

    // Validate file upload if upload option is selected
    if (emailStyling.emailCampaignLogo === "upload" && !selectedFile) {
      setUploadError("Please select a logo file to upload.");
      return;
    }

    try {
      setStylingLoading(true);
      setStylingSuccess(null);
      setError(null);
      setUploadError(null);

      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setStylingLoading(false);
        setError("Request timed out. Please try again.");
      }, 30000); // 30 second timeout

      const emailCampaignService = new EmailCampaignService();
      let logoValue = emailStyling.emailCampaignLogo;

      // Upload logo file if one is selected
      if (emailStyling.emailCampaignLogo === "upload" && selectedFile) {
        try {
          // Use the same S3 bucket as the old WEB implementation
          const bucketName =
            process.env.REACT_APP_EMAIL_MANAGEMENT_BUCKET_NAME || "";
          console.log("S3 Bucket Name:", bucketName);
          console.log("Selected File:", selectedFile);

          if (!bucketName) {
            throw new Error(
              "Amazon S3 bucket is not configured. Please check REACT_APP_EMAIL_MANAGEMENT_BUCKET_NAME environment variable."
            );
          }

          // Use same file path structure as old WEB implementation: merchant/{8-digit-id}/filename.jpg
          const paddedBusinessId =
            businessData?.BusinessId?.toString()?.padStart(8, "0");
          const key = `merchant/${paddedBusinessId}/emailcampaign_logo_${Date.now()}.jpg`;
          const uploadedUrl = await UploadFile(
            selectedFile,
            bucketName,
            key,
            process.env.REACT_APP_S3_BASE_URL
          );

          if (uploadedUrl) {
            logoValue = uploadedUrl;
          } else {
            throw new Error("Failed to upload logo");
          }
        } catch (uploadError) {
          console.error("Error uploading logo:", uploadError);
          const errorMessage =
            uploadError instanceof Error
              ? uploadError.message
              : "Failed to upload logo. Please try again.";
          setUploadError(`Upload failed: ${errorMessage}`);
          clearTimeout(timeoutId);
          setStylingLoading(false);
          return;
        }
      }

      const request: SaveEmailCampaignConfigsRequest = {
        BusinessId: businessData.BusinessId,
        HeaderBgColor: emailStyling.headerBgColor,
        HeaderFontColor: emailStyling.headerFontColor,
        ContentBorderColor: emailStyling.contentBorderColor,
        EmailCampaignLogo: logoValue
      };

      console.log("Saving email styling with request:", request);

      const response = await emailCampaignService.saveEmailCampaignConfigs(
        request
      );

      console.log("Save response:", response);

      // Clear timeout since request completed
      clearTimeout(timeoutId);

      if (response && response.Success) {
        setStylingSuccess("Email styling saved successfully!");

        // Reset upload state after successful save
        setSelectedFile(null);
        setLogoPreview(null);

        // Reload settings to get updated data
        try {
          const updatedSettings =
            await emailCampaignService.loadMerchantEmailCampaignSettings(
              businessData.BusinessId
            );
          setMerchantSettings(updatedSettings);
        } catch (loadError) {
          console.warn("Failed to reload settings after save:", loadError);
          // Don't fail the whole operation if reload fails
        }

        // Clear success message after 3 seconds
        setTimeout(() => setStylingSuccess(null), 3000);
      } else {
        console.error("Save response indicates failure:", response);
        setError("Failed to save email styling. Please try again.");
      }
    } catch (error) {
      console.error("Error saving email styling:", error);
      setError("Failed to save email styling. Please try again.");
    } finally {
      setStylingLoading(false);
      console.log("Loading state reset to false");
    }
  };

  const handlePreviewStyling = () => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  // Validation function for styling save button
  const isStylingValid = (): boolean => {
    // Basic validation - at least one styling option should be configured
    return (
      emailStyling.headerBgColor !== "" &&
      emailStyling.headerFontColor !== "" &&
      emailStyling.contentBorderColor !== ""
    );
  };

  const isLogoUploadValid = (): boolean => {
    // If upload option is selected, file must be selected
    if (emailStyling.emailCampaignLogo === "upload") {
      return selectedFile !== null && !uploadError;
    }
    return true;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  // Get status badge configuration based on Processed value
  const getStatusConfig = (processed: number) => {
    switch(processed) {
      case -1:
      case 0:
        return { 
          label: 'Scheduled', 
          color: '#2196F3',      // Blue
          bgColor: '#E3F2FD',    // Light blue background
          textColor: '#1565C0'   // Dark blue text
        };
      case 1:
        return { 
          label: 'Sending', 
          color: '#FF9800',      // Orange
          bgColor: '#FFF3E0',    // Light orange background
          textColor: '#E65100'   // Dark orange text
        };
      case 2:
        return { 
          label: 'Sent', 
          color: '#4CAF50',      // Green
          bgColor: '#E8F5E9',    // Light green background
          textColor: '#2E7D32'   // Dark green text
        };
      default:
        return { 
          label: 'Unknown', 
          color: '#9E9E9E',      // Gray
          bgColor: '#F5F5F5',    // Light gray background
          textColor: '#616161'   // Dark gray text
        };
    }
  };

  // Column definitions for scheduled campaigns
  const scheduledColumns = useMemo(
    () => [
      {
        field: "SendOutDateTime",
        headerName: "Send Date",
        width: 200,
        valueGetter: (params: any) => formatDate(params.row.SendOutDateTime),
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell"
      },
      {
        field: "CreationDate",
        headerName: "Creation Date",
        width: 200,
        valueGetter: (params: any) =>
          params.row.DateCreated ? formatDate(params.row.DateCreated) : "-",
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell"
      },
      {
        field: "EmailSubject",
        headerName: "Subject",
        width: 350,
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell",
        renderCell: (params: any) => (
          <Box
            sx={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        )
      },
      // {
      //   field: "Processed",
      //   headerName: "Status",
      //   width: 130,
      //   headerClassName: "customTableHeader",
      //   cellClassName: "customTableCell",
      //   renderCell: (params: any) => {
      //     const status = getStatusConfig(params.row.Processed);
      //     return (
      //       <Chip
      //         label={status.label}
      //         size="small"
      //         sx={{
      //           backgroundColor: status.bgColor,
      //           color: status.textColor,
      //           fontWeight: 600,
      //           fontSize: '0.75rem',
      //           height: '24px',
      //           borderRadius: '12px',
      //           '& .MuiChip-label': {
      //             px: 1.5
      //           }
      //         }}
      //       />
      //     );
      //   }
      // },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        renderCell: (params: any) => (
          <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <CustomButton
              size="small"
              variant="outlined"
              onClick={() =>
                navigate(`/emailcampaigns/edit/${params.row.CampaignId}`)
              }
              disabled={isNonPaying || isBillingLoading}
            >
              Edit
            </CustomButton>
          </Box>
        ),
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell"
      }
    ],
    [navigate, getStatusConfig, isNonPaying, isBillingLoading]
  );

  // Column definitions for previous campaigns
  const previousColumns = useMemo(
    () => [
      {
        field: "SendOutDateTime",
        headerName: "Send Date",
        width: 200,
        valueGetter: (params: any) => formatDate(params.row.SendOutDateTime),
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell"
      },
      // {
      //   field: "CreationDate",
      //   headerName: "Creation Date",
      //   width: 200,
      //   valueGetter: (params: any) =>
      //     params.row.DateCreated ? formatDate(params.row.DateCreated) : "-",
      //   headerClassName: "customTableHeader",
      //   cellClassName: "customTableCell"
      // },
      {
        field: "EmailSubject",
        headerName: "Subject",
        width: 280,
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell",
        renderCell: (params: any) => (
          <Box
            sx={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        )
      },
      // {
      //   field: "Processed",
      //   headerName: "Status",
      //   width: 130,
      //   headerClassName: "customTableHeader",
      //   cellClassName: "customTableCell",
      //   renderCell: (params: any) => {
      //     const status = getStatusConfig(params.row.Processed);
      //     return (
      //       <Chip
      //         label={status.label}
      //         size="small"
      //         sx={{
      //           backgroundColor: status.bgColor,
      //           color: status.textColor,
      //           fontWeight: 600,
      //           fontSize: '0.75rem',
      //           height: '24px',
      //           borderRadius: '12px',
      //           '& .MuiChip-label': {
      //             px: 1.5
      //           }
      //         }}
      //       />
      //     );
      //   }
      // },
      {
        field: "TotalSent",
        headerName: "Email Sent To",
        width: 150,
        valueGetter: (params: any) =>
          openRates[params.row.CampaignId]?.TotalSent || "-",
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell",
        renderCell: (params: any) => {
          const campaignId = params.row.CampaignId;
          const rateData = openRates[campaignId];
          console.log(`Rendering TotalSent for ${campaignId}:`, rateData);

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!rateData && (
                <CircularProgress size={12} sx={{ color: "#192f67" }} />
              )}
              <span>{rateData?.TotalSent || "-"}</span>
            </Box>
          );
        }
      },
      {
        field: "OpenRate",
        headerName: "Open Rate",
        width: 180,
        valueGetter: (params: any) =>
          openRates[params.row.CampaignId]
            ? `${openRates[params.row.CampaignId].OpenRate}%`
            : "Loading...",
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell",
        renderCell: (params: any) => {
          const campaignId = params.row.CampaignId;
          const rateData = openRates[campaignId];
          console.log(`Rendering OpenRate for ${campaignId}:`, rateData);

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!rateData && (
                <CircularProgress size={12} sx={{ color: "#192f67" }} />
              )}
              <span>{rateData ? `${rateData.OpenRate}%` : "Loading..."}</span>
            </Box>
          );
        }
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        renderCell: (params: any) => (
          <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <CustomButton
              size="small"
              variant="outlined"
              onClick={() => handleResendCampaign(params.row.CampaignId)}
              disabled={isNonPaying || isBillingLoading}
            >
              Duplicate
            </CustomButton>
          </Box>
        ),
        headerClassName: "customTableHeader",
        cellClassName: "customTableCell"
      }
    ],
    [openRates, handleResendCampaign, getStatusConfig, isNonPaying, isBillingLoading]
  );

  return (
    <CommonPageLayout
      heading="Email Campaigns"
      subHeading=""
      docs={
        <CustomButton
          variant="contained"
          className="blue_button"
          onClick={() => navigate("/emailcampaigns/create")}
          disabled={isNonPaying || isBillingLoading}
        >
          Create new campaign
        </CustomButton>
      }
    >
      {loading ? (
        <Loader />
      ) : (
        <Box>
          {isNonPaying && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error">
                Access limited: Please renew your contract or fix payment issues
                to manage email campaigns.
              </Alert>
            </Box>
          )}
          {error && (
            <div ref={errorRef} tabIndex={-1} aria-live="assertive">
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            </div>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography>
              A broadcast email is a great way to let your customers know about
              events or promotions in your business. Once created, the email
              will arrive directly into your customers' inbox.
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#192F67" }}
              >
                Scheduled
              </Typography>
              <RefreshButton onClick={loadCampaigns} loading={loading} />
            </Box>

            {scheduledCampaigns.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <img src={SadFaceImage} alt="No campaigns" />
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  You have no scheduled campaign.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Press 'Create new campaign' button above to schedule a new
                  campaign.
                </Typography>
              </Box>
            ) : (
              <CustomDataTable
                rows={scheduledCampaigns}
                columns={scheduledColumns}
                initialState={{
                  pagination: { paginationModel: { page: 0, pageSize: 10 } }
                }}
                pageSizeOptions={[10, 50, 100]}
                getRowId={(row: any) => row.CampaignId}
                rowCount={scheduledCampaigns.length}
                onRowClick={() => {}}
                onPaginationModelChange={() => {}}
              />
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              Previously mailed
            </Typography>

            {previousCampaigns.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No previous campaigns found.
                </Typography>
              </Box>
            ) : (
              <CustomDataTable
                rows={previousCampaigns}
                columns={previousColumns}
                initialState={{
                  pagination: { paginationModel: { page: 0, pageSize: 10 } }
                }}
                pageSizeOptions={[10, 50, 100]}
                getRowId={(row: any) => row.CampaignId}
                rowCount={previousCampaigns.length}
                onRowClick={() => {}}
                onPaginationModelChange={() => {}}
              />
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              Email Styling
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customise the colours that will appear on the email.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <input
                    type="color"
                    id="border-color"
                    value={emailStyling.contentBorderColor}
                    onChange={(e) =>
                      handleStylingChange("contentBorderColor", e.target.value)
                    }
                    title="Border colour picker"
                    aria-label="Border colour"
                  />
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="border-color"
                    sx={{ ml: 1 }}
                  >
                    Border colour
                  </Typography>
                </Box>
                <Box>
                  <input
                    type="color"
                    id="font-color"
                    value={emailStyling.headerFontColor}
                    onChange={(e) =>
                      handleStylingChange("headerFontColor", e.target.value)
                    }
                    title="Font colour picker"
                    aria-label="Font colour"
                  />
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="font-color"
                    sx={{ ml: 1 }}
                  >
                    Font colour
                  </Typography>
                </Box>
                <Box>
                  <input
                    type="color"
                    id="header-bg-color"
                    value={emailStyling.headerBgColor}
                    onChange={(e) =>
                      handleStylingChange("headerBgColor", e.target.value)
                    }
                    title="Header background colour picker"
                    aria-label="Header background colour"
                  />
                  <Typography
                    variant="caption"
                    component="label"
                    htmlFor="header-bg-color"
                    sx={{ ml: 1 }}
                  >
                    Header background colour
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the logo you would like to be placed in the header of
                your email.
              </Typography>

              {settingsLoading && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <CircularProgress size={16} sx={{ color: "#192f67" }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading settings...
                  </Typography>
                </Box>
              )}

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}
              >
                <select
                  className="logo-selector"
                  aria-label="Select logo for email header"
                  value={emailStyling.emailCampaignLogo}
                  disabled={settingsLoading}
                  onChange={(e) =>
                    handleStylingChange("emailCampaignLogo", e.target.value)
                  }
                >
                  <option value="">Select logo</option>
                  <option value="upload">Upload new Logo</option>
                  {(merchantSettings as any)?.MerchantLogo && (
                    <option value={(merchantSettings as any).MerchantLogo}>
                      {(merchantSettings as any).MerchantLogo.split("/").pop()}
                    </option>
                  )}
                  {merchantSettings?.EmailCampaignLogo &&
                    merchantSettings.EmailCampaignLogo !== "no logo" &&
                    merchantSettings.EmailCampaignLogo !==
                      (merchantSettings as any)?.MerchantLogo && (
                      <option value={merchantSettings.EmailCampaignLogo}>
                        {merchantSettings.EmailCampaignLogo.split("/").pop()}
                      </option>
                    )}
                </select>

                {/* Logo Preview - Aligned with select input */}
                {emailStyling.emailCampaignLogo &&
                  emailStyling.emailCampaignLogo !== "" &&
                  emailStyling.emailCampaignLogo !== "upload" && (
                    <Box
                      sx={{
                        p: 1,
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        backgroundColor: "#f9f9f9"
                      }}
                    >
                      <Box
                        component="img"
                        src={emailStyling.emailCampaignLogo}
                        alt="Logo preview"
                        sx={{
                          width: 60,
                          height: 60,
                          objectFit: "contain",
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          backgroundColor: "#fff"
                        }}
                      />
                    </Box>
                  )}
              </Box>

              {/* File upload section - only show when upload option is selected */}
              {emailStyling.emailCampaignLogo === "upload" && (
                <Box sx={{ mt: 2 }}>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/jpeg,image/jpg"
                    onChange={handleFileSelect}
                    className="logo-upload-input"
                  />
                  <label htmlFor="logo-upload">
                    <CustomButton
                      variant="outlined"
                      component="span"
                      sx={{ mb: 2 }}
                    >
                      Choose Logo File
                    </CustomButton>
                  </label>

                  {/* File validation error */}
                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {uploadError}
                    </Alert>
                  )}

                  {/* Selected file preview */}
                  {selectedFile && logoPreview && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Selected file: {selectedFile.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          border: "1px solid #ddd",
                          borderRadius: 1,
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Box
                          component="img"
                          src={logoPreview}
                          alt="Logo preview"
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: "contain",
                            border: "1px solid #ccc",
                            borderRadius: 1
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        <CustomButton
                          size="small"
                          variant="text"
                          color="error"
                          onClick={handleRemoveFile}
                        >
                          Remove
                        </CustomButton>
                      </Box>
                    </Box>
                  )}

                  {/* Upload instructions */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Supported formats: JPEG. Maximum file size: 5MB
                  </Typography>
                </Box>
              )}
            </Box>

            {stylingSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {stylingSuccess}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
              <CustomButton
                variant="contained"
                className="blue_button"
                onClick={handleSaveStyling}
                disabled={
                  stylingLoading || !isStylingValid() || !isLogoUploadValid() || isNonPaying || isBillingLoading
                }
              >
                {stylingLoading ? "Saving..." : "Save"}
              </CustomButton>
              <CustomButton variant="outlined" onClick={handlePreviewStyling}>
                Preview
              </CustomButton>
              {stylingLoading && (
                <CustomButton
                  variant="text"
                  color="error"
                  onClick={() => setStylingLoading(false)}
                  size="small"
                >
                  Cancel
                </CustomButton>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Email Preview</Typography>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Box
              sx={{
                width: "650px",
                border: `1px solid ${emailStyling.contentBorderColor}`,
                borderRadius: "4px",
                backgroundColor: "#fff",
                fontFamily:
                  "'Helvetica Neue', Helvetica, Arial, Verdana, sans-serif",
                fontSize: "12px"
              }}
            >
              {/* Email Header */}
              <Box
                sx={{
                  backgroundColor: emailStyling.headerBgColor,
                  color: emailStyling.headerFontColor,
                  textAlign: "center",
                  py: 4,
                  px: 2
                }}
              >
                <Box sx={{ fontSize: "32px", mb: 2 }}>&nbsp;</Box>
                {emailStyling.emailCampaignLogo &&
                  emailStyling.emailCampaignLogo !== "" && (
                    <Box
                      component="img"
                      src={
                        emailStyling.emailCampaignLogo === "upload" &&
                        logoPreview
                          ? logoPreview
                          : emailStyling.emailCampaignLogo !== "upload"
                          ? emailStyling.emailCampaignLogo
                          : "/common/images/laptop-placeholder.png"
                      }
                      alt="Logo"
                      sx={{
                        width: "96px",
                        height: "96px",
                        border: "8px solid rgba(0,0,0,0.15)",
                        borderRadius: "4px",
                        mb: 2,
                        objectFit: "contain"
                      }}
                    />
                  )}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: emailStyling.headerFontColor,
                    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
                  }}
                >
                  {merchantSettings?.MerchantEstablishmentName ||
                    "Your Business Name"}
                </Typography>
                <Box sx={{ fontSize: "18px", mt: 2 }}>&nbsp;</Box>
              </Box>

              {/* Email Content */}
              <Box
                sx={{
                  p: 6,
                  borderTop: `1px solid ${emailStyling.contentBorderColor}`,
                  textAlign: "left",
                  minHeight: "200px"
                }}
              >
                <Typography>Your email content will appear here...</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </CommonPageLayout>
  );
};

export default EmailCampaigns;
